'use client';
import React from 'react';
import { TypePlayKitPageTranslations } from '@/utils/translations/types/TypePlayKitPageTranslations';
import { HeaderText } from '@/components';

import { HeaderSize } from '@/components/ui/HeaderText';
import {
  generateBookAddonLink,
  getBookBundleUpsellProductDetails,
  TypeScheduledItemGroup,
} from '@/components/account/PlayKitSchedule/utils';
import {
  BookBundleUpsell,
  PrepayUpsellBanners,
} from '@/components/account/PlayKitSchedule/components';
import useLocale from '@/hooks/useLocale';
import interpolate from '@/utils/translations/interpolate';
import { formatMoney } from '@/utils/helpers';
import { PlayKitCard } from '@/components/account';
import { TypePlayKitBundleOffer } from '@/lib/api/productCatalog/types';
import { TypeBanner } from '@/core/generated-types';

export interface UpcomingPlayKitProps {
  t: TypePlayKitPageTranslations;
  group: TypeScheduledItemGroup;
  isFirstGroup?: boolean;
  prepayBundleExtensionOffers: TypePlayKitBundleOffer[];
  prepayBanner?: TypeBanner;
  holidayBanner?: TypeBanner;
}

function UpcomingPlayKit({
  t,
  group,
  isFirstGroup,
  prepayBundleExtensionOffers,
  prepayBanner,
  holidayBanner,
}: UpcomingPlayKitProps) {
  const { shouldShowBookBundleUpsell, bookBundleUpsellProducts } =
    getBookBundleUpsellProductDetails(group);
  const locale = useLocale();

  return (
    <div data-testid="upcomingPlayKit">
      {isFirstGroup ? (
        <HeaderText
          classes="text-3xl md:text-4xl pb-1 md:pb-2"
          size={HeaderSize.H2}
          text={t.upcomingPlayKits}
        />
      ) : null}

      <PrepayUpsellBanners
        group={group}
        prepayBundleExtensionOffers={prepayBundleExtensionOffers}
        prepayBanner={prepayBanner}
        holidayBanner={holidayBanner}
      />

      {group.items.map((item, index) => {
        const isLastKitInGroup = index === group.items.length - 1;

        return (
          <div key={item.uuid} className="rounded-md p-6 mb-10 shadow-lg">
            <PlayKitCard
              t={t}
              key={item.uuid}
              scheduledItem={item}
              isLastKitInGroup={isLastKitInGroup}
            >
              {isLastKitInGroup && shouldShowBookBundleUpsell && (
                <BookBundleUpsell
                  title={t.bookBundleAddOn}
                  linkHref={generateBookAddonLink(group, locale)}
                  linkCopy={interpolate(t.addBookBundles, {
                    bundleAmount: formatMoney(
                      bookBundleUpsellProducts[0].metadata.price_cents / 100,
                      locale,
                      true,
                    ),
                  })}
                  bookBundleUpsellProducts={bookBundleUpsellProducts}
                />
              )}
            </PlayKitCard>
          </div>
        );
      })}
    </div>
  );
}

export default UpcomingPlayKit;
